import { css } from '@emotion/react'
import styled from '@emotion/styled'

import type { EmbedFeedParams, IAnyWidgetParams } from '@core/types'
import { WidgetSizeEnum } from '@core/types'
import { BORDER_RADIUS } from '@embed/styled'

import { DEFAULT_THUMBNAIL_GAP_PX } from './GridConstants'

const getDynamicSpacing = (size?: WidgetSizeEnum): string => {
  switch (size) {
    case WidgetSizeEnum.MEDIUM:
      return '20px'
    case WidgetSizeEnum.LARGE:
      return '24px'
    case WidgetSizeEnum.SMALL:
      return '18px'
    default:
      return `${DEFAULT_THUMBNAIL_GAP_PX}px`
  }
}

export const GridWrapper = styled.div<{
  borderStyle: IAnyWidgetParams['ui_border_style']
}>`
  color: white;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;
  max-width: 100vw;
  overflow: auto;

  --border-radius: var(
    --thumbnail-border-radius,
    ${(props) => (props.borderStyle === 'hard' ? '0' : BORDER_RADIUS)}
  );
`

export const GridInner = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: var(--thumbnail-justify, center);
`

export const Grid = styled.div<{
  columns: number
  gridMode: EmbedFeedParams['mode']
  isStoriesLayout: boolean
  pulseAnimation: boolean | undefined
  widgetSize: WidgetSizeEnum | undefined
  /** Duplicated inner row + WAAPI transform marquee (`horizontal_auto_scroll_interval_ms`, row only) */
  $horizontalAutoScroll?: boolean
  $horizontalAutoScrollUsesScrollPosition?: boolean
}>`
  ${(props) =>
    !props.$horizontalAutoScroll &&
    css`
      transform: translateZ(0);
    `}
  box-sizing: border-box;
  display: flex;
  flex-flow: row nowrap;
  overflow-scrolling: touch;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 0;
  margin: 0;
  scroll-behavior: smooth;

  ${(props) =>
    props.$horizontalAutoScroll &&
    props.gridMode !== 'grid' &&
    css`
      flex: 1 1 0;
      min-width: 0;
      width: 100%;
      align-self: stretch;
      overflow-x: ${
        props.$horizontalAutoScrollUsesScrollPosition ? 'scroll' : 'hidden'
      };
      gap: 0;
      scroll-behavior: auto;
      scroll-snap-type: none;
      scroll-snap-stop: normal;

      & > * {
        scroll-snap-align: none;
      }
    `}

  ${(props) =>
    props.pulseAnimation && props.isStoriesLayout
      ? css`
          gap: var(--thumbnail-gap, ${getDynamicSpacing(props.widgetSize)});
          padding: ${getDynamicSpacing(props.widgetSize)};
        `
      : css`
          gap: var(--thumbnail-gap, ${DEFAULT_THUMBNAIL_GAP_PX}px);
          padding: 0;
        `}

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  ${(props) =>
    (props.gridMode === 'pinned' ||
      (props.gridMode === 'row' && !props.isStoriesLayout)) &&
    css`
      border-radius: var(--border-radius);
    `}

  ${(props) =>
    props.gridMode === 'grid' &&
    css`
      display: grid !important;
      grid-template-columns: repeat(${props.columns}, 1fr);
      position: relative;
      left: auto;
      right: auto;
      width: 100%;
    `}
`

export const LoadMoreButton = styled('button')`
  align-self: center;
  background: rgb(128 128 128 / 80%);
  border-radius: ${BORDER_RADIUS};
  border: none;
  color: white;
  font-size: 1.4em;
  grid-column: 1 / -1;
  line-height: 2em;
  margin: 1em;
  outline: none;
  padding: 0 2em;
  white-space: nowrap;
`

export const GridRowSlot = styled('slot')`
  &::slotted(*) {
    grid-column: 1 / -1;
  }
`
